/**
 * REST request schemas (validated on the server, reused for client-side form validation)
 * and response DTO types. Base path: /api/v1
 */
import { z } from 'zod';

export const GAME_TYPES = ['CALL_BREAK', 'SPADES', 'TARNEEB', 'HEARTS', 'FIVE_HUNDRED', 'JUDGEMENT', 'RUMMY', 'EUCHRE', 'GIN_RUMMY', 'TWENTY_NINE', 'CRAZY_EIGHTS', 'CRIBBAGE', 'BLACKJACK', 'BHABHI', 'PINOCHLE', 'ANDAR_BAHAR', 'BRIDGE', 'SEVENS', 'SCOPA', 'BACCARAT', 'BLUFF', 'DRAGON_TIGER', 'FLUSH', 'COURT_PIECE', 'DEHLA_PAKAD', 'WHIST', 'SEEP', 'BELOTE', 'DURAK', 'PRESIDENT', 'BRISCOLA', 'CASINO_WAR', 'THREE_CARD_POKER', 'GO_FISH', 'TRESSETTE', 'RED_DOG', 'CANASTA', 'SKAT', 'PAI_GOW', 'BIG_TWO', 'WAR', 'RUMMY_500', 'BRAG', 'PITCH', 'HAZARI', 'MARRIAGE', 'TEEN_PATTI', 'POKER'] as const;
export const gameTypeSchema = z.enum(GAME_TYPES);
export type GameTypeDto = z.infer<typeof gameTypeSchema>;

export const ROLES = ['PLAYER', 'MODERATOR', 'SUPPORT', 'ADMIN', 'SUPER_ADMIN'] as const;
export type Role = (typeof ROLES)[number];

export const usernameSchema = z
  .string()
  .trim()
  .min(3)
  .max(20)
  .regex(/^[a-zA-Z0-9_]+$/, 'Letters, numbers and underscore only');
export const passwordSchema = z.string().min(8).max(128);
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const phoneSchema = z.string().trim().regex(/^\+[1-9]\d{7,14}$/, 'Use international format, e.g. +9779812345678');

export const deviceIdSchema = z.string().min(8).max(128).optional();

// ---------------- Auth ----------------
export const registerSchema = z
  .object({
    username: usernameSchema,
    password: passwordSchema,
    email: emailSchema.optional(),
    avatar: z.string().max(64).optional(),
    referralCode: z.string().trim().max(16).optional(),
    deviceId: deviceIdSchema,
    language: z.enum(['en', 'ne', 'hi']).optional(),
  })
  .strict();
export type RegisterRequest = z.infer<typeof registerSchema>;

export const guestSchema = z.object({ deviceId: deviceIdSchema, language: z.enum(['en', 'ne', 'hi']).optional() }).strict();
export type GuestRequest = z.infer<typeof guestSchema>;

export const loginSchema = z
  .object({
    identifier: z.string().trim().min(3).max(254), // username or email
    password: z.string().min(1).max(128),
    /** 6-digit authenticator code when two-factor authentication is enabled. */
    mfaCode: z.string().regex(/^\d{6}$/).optional(),
    deviceId: deviceIdSchema,
  })
  .strict();
export type LoginRequest = z.infer<typeof loginSchema>;

export const otpRequestSchema = z.object({ phone: phoneSchema }).strict();
export const otpVerifySchema = z
  .object({
    phone: phoneSchema,
    code: z.string().regex(/^\d{6}$/),
    username: usernameSchema.optional(),
    referralCode: z.string().trim().max(16).optional(),
    deviceId: deviceIdSchema,
  })
  .strict();

export const socialLoginSchema = z
  .object({
    provider: z.enum(['google']),
    idToken: z.string().min(10).max(4096),
    username: usernameSchema.optional(),
    referralCode: z.string().trim().max(16).optional(),
    deviceId: deviceIdSchema,
  })
  .strict();

export const refreshSchema = z.object({ refreshToken: z.string().min(20).max(512).optional() }).strict();

export interface AuthTokens {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

export interface AuthResponse {
  user: MeDto;
  tokens: AuthTokens;
  /** Present on first registration. */
  signupBonus?: { amount: number; status: 'GRANTED' | 'PENDING_REVIEW' | 'NOT_ELIGIBLE' };
}

// ---------------- Users ----------------
export interface PublicUserDto {
  id: string;
  username: string;
  avatar: string;
  level: number;
  isBot?: boolean;
}

export interface MeDto extends PublicUserDto {
  mfaEnabled: boolean;
  email: string | null;
  phone: string | null;
  role: Role;
  xp: number;
  xpForNextLevel: number;
  coins: number;
  referralCode: string;
  favoriteGame: GameTypeDto | null;
  settings: UserSettings;
  onboardingCompleted: boolean;
  createdAt: string;
}

/** Cosmetics bought with real money; `price` is USD cents (0 = owned by everyone). Purely visual: never affects gameplay. */
export const CARD_BACKS = [
  { id: 'classic', level: 1, price: 0 },
  { id: 'crimson', level: 3, price: 99 },
  { id: 'midnight', level: 5, price: 199 },
  { id: 'saffron', level: 8, price: 299 },
  { id: 'himalaya', level: 12, price: 499 },
  { id: 'gold', level: 20, price: 999 },
] as const;
export const TABLE_FELTS = [
  { id: 'emerald', level: 1, price: 0 },
  { id: 'royal', level: 4, price: 149 },
  { id: 'maroon', level: 7, price: 199 },
  { id: 'charcoal', level: 10, price: 399 },
  { id: 'teal', level: 15, price: 699 },
] as const;
export type CosmeticKind = 'CARD_BACK' | 'TABLE_FELT';
export type CardBackId = (typeof CARD_BACKS)[number]['id'];
export type TableFeltId = (typeof TABLE_FELTS)[number]['id'];

export const userSettingsSchema = z
  .object({
    language: z.enum(['en', 'ne', 'hi']),
    sound: z.boolean(),
    music: z.boolean(),
    masterVolume: z.number().min(0).max(1),
    musicVolume: z.number().min(0).max(1),
    effectsVolume: z.number().min(0).max(1),
    haptics: z.boolean(),
    graphics: z.enum(['low', 'medium', 'high']),
    reducedMotion: z.boolean(),
    highContrast: z.boolean(),
    fontScale: z.number().min(0.85).max(1.6),
    colorBlindCards: z.boolean(),
    theme: z.enum(['dark', 'light', 'system']),
    cardBack: z.enum(CARD_BACKS.map((c) => c.id) as [CardBackId, ...CardBackId[]]),
    tableFelt: z.enum(TABLE_FELTS.map((c) => c.id) as [TableFeltId, ...TableFeltId[]]),
    /** Games the player pinned to the top of the lobby. */
    favoriteGames: z.array(gameTypeSchema).max(40),
    notifications: z.object({
      friendRequests: z.boolean(),
      gameInvites: z.boolean(),
      dailyReward: z.boolean(),
      missions: z.boolean(),
      tournaments: z.boolean(),
      yourTurn: z.boolean(),
      promotions: z.boolean(),
    }),
    privacy: z.object({
      showOnlineStatus: z.boolean(),
      allowFriendRequests: z.boolean(),
      showInLeaderboards: z.boolean(),
      analyticsConsent: z.boolean(),
    }),
  })
  .strict();
export type UserSettings = z.infer<typeof userSettingsSchema>;
export const updateSettingsSchema = userSettingsSchema
  .extend({
    notifications: userSettingsSchema.shape.notifications.partial(),
    privacy: userSettingsSchema.shape.privacy.partial(),
  })
  .partial();
export type UpdateSettingsRequest = z.infer<typeof updateSettingsSchema>;

export const DEFAULT_SETTINGS: UserSettings = {
  language: 'en',
  sound: true,
  music: true,
  masterVolume: 0.8,
  musicVolume: 0.5,
  effectsVolume: 0.8,
  haptics: true,
  graphics: 'high',
  reducedMotion: false,
  highContrast: false,
  fontScale: 1,
  colorBlindCards: false,
  theme: 'system',
  cardBack: 'classic',
  tableFelt: 'emerald',
  favoriteGames: [],
  notifications: {
    friendRequests: true,
    gameInvites: true,
    dailyReward: true,
    missions: true,
    tournaments: true,
    yourTurn: true,
    promotions: false,
  },
  privacy: { showOnlineStatus: true, allowFriendRequests: true, showInLeaderboards: true, analyticsConsent: false },
};

export const updateProfileSchema = z
  .object({
    username: usernameSchema.optional(),
    avatar: z.string().max(64).optional(),
    favoriteGame: gameTypeSchema.nullable().optional(),
    onboardingCompleted: z.boolean().optional(),
  })
  .strict();

export interface ProfileDto extends PublicUserDto {
  createdAt: string;
  stats: GameStatsDto[];
  achievements: { code: string; name: string; icon: string; unlockedAt: string }[];
  ratings: { gameType: GameTypeDto; rating: number; gamesRated: number }[];
  friendship?: 'NONE' | 'FRIENDS' | 'REQUEST_SENT' | 'REQUEST_RECEIVED' | 'BLOCKED';
  online?: boolean;
}

export interface GameStatsDto {
  gameType: GameTypeDto;
  played: number;
  won: number;
  winRate: number;
  coinsWon: number;
  bestScore: number | null;
}

// ---------------- Pagination ----------------
export const pageQuerySchema = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

// ---------------- Games / tables ----------------
export interface GameSummaryDto {
  type: GameTypeDto;
  name: string;
  enabled: boolean;
  minPlayers: number;
  maxPlayers: number;
  onlinePlayers: number;
  activeTables: number;
  minEntry: number;
  maxEntry: number;
  kind: 'match' | 'session';
  tables: TableConfigDto[];
}

export interface TableConfigDto {
  id: string;
  gameType: GameTypeDto;
  variant: string;
  name: string;
  tier: string;
  minPlayers: number;
  maxPlayers: number;
  entryFee: number;
  minBalance: number;
  maxBalance: number | null;
  turnTimerSec: number;
  botAllowed: boolean;
  region: string;
  language: string | null;
  privacy: 'PUBLIC' | 'PRIVATE';
  status: 'ACTIVE' | 'DISABLED';
  rules: Record<string, unknown>;
  /** How strong the table's bots are (offline play uses the same bots). */
  botDifficulty?: 'BEGINNER' | 'EASY' | 'NORMAL' | 'HARD' | 'EXPERT';
  onlinePlayers?: number;
}

export const tableConfigInputSchema = z
  .object({
    gameType: gameTypeSchema,
    variant: z.string().min(1).max(40),
    name: z.string().min(1).max(60),
    tier: z.string().min(1).max(30),
    minPlayers: z.number().int().min(2).max(10),
    maxPlayers: z.number().int().min(2).max(10),
    entryFee: z.number().int().min(0).max(1e12),
    minBalance: z.number().int().min(0).max(1e12),
    maxBalance: z.number().int().min(0).max(1e13).nullable(),
    turnTimerSec: z.number().int().min(5).max(120),
    botAllowed: z.boolean(),
    region: z.string().min(2).max(16),
    language: z.string().max(8).nullable(),
    privacy: z.enum(['PUBLIC', 'PRIVATE']),
    status: z.enum(['ACTIVE', 'DISABLED']),
    matchmakingTimeoutSec: z.number().int().min(3).max(120),
    botDifficulty: z.enum(['BEGINNER', 'EASY', 'NORMAL', 'HARD', 'EXPERT']),
    rules: z.record(z.string(), z.unknown()),
    sortOrder: z.number().int().min(0).max(10000).default(0),
  })
  .strict();
export type TableConfigInput = z.infer<typeof tableConfigInputSchema>;

// ---------------- Wallet ----------------
export const TX_TYPES = [
  'SIGNUP_BONUS',
  'DAILY_REWARD',
  'MISSION_REWARD',
  'ACHIEVEMENT_REWARD',
  'AD_REWARD',
  'REFERRAL_REWARD',
  'EVENT_REWARD',
  'TOURNAMENT_ENTRY',
  'TOURNAMENT_PRIZE',
  'TOURNAMENT_REFUND',
  'MATCH_ENTRY',
  'MATCH_PAYOUT',
  'MATCH_REFUND',
  'TABLE_BUYIN',
  'TABLE_CASHOUT',
  'PURCHASE',
  'COSMETIC_PURCHASE',
  'ROOM_FEE',
  'ADMIN_ADJUSTMENT',
  'PROMOTION',
  'RECOVERY_REFUND',
  'OFFLINE_REWARD',
  'OFFLINE_RESULT',
] as const;
export type TxType = (typeof TX_TYPES)[number];

export interface CoinTransactionDto {
  id: string;
  type: TxType;
  amount: number;
  balanceAfter: number;
  referenceId: string | null;
  description: string;
  createdAt: string;
}

export interface WalletDto {
  balance: number;
  lifetimeEarned: number;
  lifetimeSpent: number;
}

// ---------------- Matchmaking ----------------
export const quickPlaySchema = z
  .object({
    tableConfigId: z.string().uuid().optional(),
    gameType: gameTypeSchema.optional(),
    region: z.string().max(16).optional(),
    language: z.string().max(8).optional(),
    pingMs: z.number().int().min(0).max(10000).optional(),
  })
  .strict()
  .refine((v) => v.tableConfigId || v.gameType, 'tableConfigId or gameType required');

// ---------------- Rooms ----------------
export const createRoomSchema = z
  .object({
    tableConfigId: z.string().uuid(),
    maxPlayers: z.number().int().min(2).max(10).optional(),
    entryFee: z.number().int().min(0).max(1e12).optional(),
    password: z.string().min(4).max(32).optional(),
    fillWithBots: z.boolean().default(true),
    rules: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const updateRoomSchema = z
  .object({
    maxPlayers: z.number().int().min(2).max(10).optional(),
    entryFee: z.number().int().min(0).max(1e12).optional(),
    password: z.string().min(4).max(32).nullable().optional(),
    fillWithBots: z.boolean().optional(),
    locked: z.boolean().optional(),
  })
  .strict();

export const joinRoomSchema = z.object({ password: z.string().max(32).optional() }).strict();

export interface RoomDto {
  id: string;
  code: string;
  inviteUrl: string;
  gameType: GameTypeDto;
  tableConfigId: string;
  tableName: string;
  ownerId: string;
  status: 'OPEN' | 'STARTING' | 'IN_GAME' | 'CLOSED' | 'CANCELLED';
  maxPlayers: number;
  entryFee: number;
  hasPassword: boolean;
  locked: boolean;
  fillWithBots: boolean;
  members: { user: PublicUserDto; seat: number | null; ready: boolean }[];
  tableId: string | null;
  createdAt: string;
}

// ---------------- Social ----------------
export const friendRequestSchema = z.object({ userId: z.string().uuid() }).strict();
export const reportSchema = z
  .object({
    userId: z.string().uuid(),
    reason: z.enum(['CHEATING', 'ABUSE', 'SPAM', 'INAPPROPRIATE_NAME', 'COLLUSION', 'OTHER']),
    details: z.string().max(1000).optional(),
    matchId: z.string().uuid().optional(),
    chatMessageId: z.string().uuid().optional(),
  })
  .strict();
export const inviteSchema = z
  .object({
    userIds: z.array(z.string().uuid()).min(1).max(10),
    roomId: z.string().uuid(),
  })
  .strict();

export interface FriendDto {
  user: PublicUserDto;
  online: boolean;
  status: 'idle' | 'in_game' | 'offline';
  /** Set while the friend plays at a public table that can be watched. */
  tableId: string | null;
  since: string;
}

// ---------------- Rewards / ads / store ----------------
export interface DailyRewardStatusDto {
  streakDay: number;
  canClaim: boolean;
  nextClaimAt: string | null;
  calendar: { day: number; coins: number; xp: number; item: string | null; claimed: boolean; today: boolean }[];
}

export interface MissionDto {
  id: string;
  code: string;
  name: string;
  description: string;
  period: 'DAILY' | 'WEEKLY' | 'EVENT';
  objective: string;
  progress: number;
  target: number;
  reward: { coins: number; xp: number };
  status: 'ACTIVE' | 'COMPLETED' | 'CLAIMED' | 'EXPIRED';
  expiresAt: string;
}

export const adRewardStartSchema = z.object({ placement: z.string().min(1).max(40) }).strict();
export interface AdRewardSessionDto {
  sessionId: string;
  provider: string;
  placement: string;
  rewardCoins: number;
  /** Opaque value the client passes to the ad SDK as `customData` so SSV can be matched. */
  customData: string;
  expiresAt: string;
  remainingToday: number;
}

export interface ProductDto {
  id: string;
  sku: string;
  name: string;
  coins: number;
  bonusCoins: number;
  priceMicros: number;
  currency: string;
  badge: string | null;
  /** Store product ids per platform: { google_play, app_store }. */
  platformSkus: Record<string, string>;
}

export const purchaseVerifySchema = z
  .object({
    platform: z.enum(['google_play', 'app_store', 'sandbox', 'paypal']),
    productId: z.string().uuid(),
    receipt: z.string().min(1).max(20000),
    idempotencyKey: z.string().min(8).max(100),
  })
  .strict();

export interface LeaderboardEntryDto {
  rank: number;
  user: PublicUserDto;
  score: number;
}

export interface NotificationDto {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  read: boolean;
  createdAt: string;
}

export const analyticsBatchSchema = z
  .object({
    events: z
      .array(
        z.object({
          name: z.string().regex(/^[a-z_]{3,48}$/),
          ts: z.number().int().optional(),
          props: z.record(z.string(), z.union([z.string().max(200), z.number(), z.boolean(), z.null()])).optional(),
        }),
      )
      .min(1)
      .max(50),
  })
  .strict();

// ---------------- Offline play ----------------
/** Coins for a finished offline Call Break match, by finishing place (1st..4th). Admin-overridable via the `offlinePlay` config. */
export const OFFLINE_PLAY_DEFAULTS = {
  enabled: true,
  rewards: [100, 40, 0, 0] as number[],
  /** Most offline coins one account can earn per UTC day. */
  dailyCap: 500,
  /** Most coins one account can net per UTC day from offline games at real tables (losses are never capped). */
  stakesDailyCap: 20000,
};

export const offlineClaimsSchema = z
  .object({
    claims: z
      .array(
        z
          .object({
            id: z.string().uuid(),
            /** The table that was played offline (absent on claims from older app versions: Call Break only). */
            tableConfigId: z.string().uuid().optional(),
            /** Replay log of the finished game (validated by the engine on the server). */
            log: z.array(z.record(z.string(), z.unknown())).min(1).max(4000).optional(),
            /** The game was started but never finished (the app was closed): the entry fee is forfeited. */
            forfeit: z.boolean().optional(),
            /** The player left a match early; the bots finished it, so no prize is paid. */
            abandoned: z.boolean().optional(),
          })
          .strict()
          .refine((c) => c.log !== undefined || (c.forfeit === true && c.tableConfigId !== undefined), { message: 'A claim needs a log (or be a forfeit)' }),
      )
      .min(1)
      .max(10),
  })
  .strict();

export interface OfflineClaimResult {
  id: string;
  status: 'credited' | 'capped' | 'duplicate' | 'rejected';
  /** Net coins applied to the wallet (negative when the player lost their stake). */
  coins: number;
  xp?: number;
}

export interface OfflineClaimsResponse {
  results: OfflineClaimResult[];
  balance: number;
}
